import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, Sparkles, CalendarClock, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [sub, setSub] = useState(null);
  const [canceling, setCanceling] = useState(false);

  const loadSub = () => { api.get("/subscriptions/status").then((r) => setSub(r.data)).catch(() => setSub(null)); };
  useEffect(() => { api.get("/products").then((r) => setData(r.data)).catch(() => {}); loadSub(); }, []);

  const checkout = async (productId) => {
    setBusy(productId);
    try {
      const r = await api.post("/payments/checkout", { product_id: productId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const cancelSub = async () => {
    setCanceling(true);
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Subscription set to cancel at period end");
      loadSub(); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); } finally { setCanceling(false); }
  };

  if (!data) return <div className="text-[var(--bone-faint)] text-sm">Loading plans…</div>;
  const plans = ["free", "student", "creator", "pro", "architect"].map((id) => ({ id, ...data.plans[id] }));
  const oneTime = data.catalog.filter((c) => c.kind === "one_time");
  const renewDate = sub?.renews_on ? new Date(sub.renews_on).toLocaleDateString() : null;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div><h1 className="text-2xl font-display font-semibold">Pricing</h1>
          <p className="text-[var(--bone-muted)] text-sm">Elite education, affordable for anybody.</p></div>
        {data.test_mode && <Badge variant="outline" className="gold-text border-[rgba(176,141,60,0.5)]">Stripe Test Mode</Badge>}
      </div>

      {sub && sub.plan !== "free" && (
        <div className="litra-card p-5 flex items-center justify-between gap-4 flex-wrap" style={{ borderColor: "rgba(20,90,67,0.45)" }} data-testid="subscription-status-card">
          <div className="flex items-center gap-3">
            <BadgeCheck className="w-6 h-6 green-text" />
            <div>
              <div className="font-display font-semibold capitalize">{sub.plan} plan · {sub.monthly_credits} credits / month</div>
              <div className="text-xs text-[var(--bone-faint)] flex items-center gap-1.5 mt-0.5">
                <CalendarClock className="w-3.5 h-3.5" />
                {sub.cancel_at_period_end
                  ? `Cancels on ${renewDate}`
                  : `Renews on ${renewDate}${sub.days_left != null ? ` · ${sub.days_left} days left` : ""}`}
              </div>
            </div>
          </div>
          {sub.cancel_at_period_end ? (
            <Badge variant="outline" className="border-[rgba(194,75,75,0.5)] text-[#C24B4B]" data-testid="subscription-canceling-badge">Canceling</Badge>
          ) : (
            <Button variant="outline" className="glass" onClick={cancelSub} disabled={canceling} data-testid="subscription-cancel-button">
              {canceling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel subscription"}
            </Button>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {plans.map((p) => {
          const current = user?.plan === p.id;
          const featured = p.id === "student";
          return (
            <div key={p.id} className={`litra-card p-5 flex flex-col ${featured ? "border-[rgba(20,90,67,0.6)]" : ""} ${p.id === "architect" ? "gold-edge" : ""}`} data-testid={`pricing-tier-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold">{p.name}</span>
                {p.id === "architect" && <Sparkles className="w-4 h-4 gold-text" />}
                {featured && <span className="mono-chip green-text">POPULAR</span>}
              </div>
              <div className="text-3xl font-display font-semibold mt-2">${p.price}<span className="text-sm text-[var(--bone-faint)]">/mo</span></div>
              {p.price_yearly && <div className="text-xs gold-text mt-0.5">or ${p.price_yearly}/year</div>}
              <ul className="mt-3 space-y-1.5 flex-1">
                {p.features.map((f, i) => (<li key={i} className="text-xs text-[var(--bone-muted)] flex gap-2"><Check className="w-3.5 h-3.5 green-text shrink-0 mt-0.5" />{f}</li>))}
              </ul>
              {p.id === "free" ? (
                <Button variant="outline" disabled className="mt-4 glass">{current ? "Current plan" : "Free"}</Button>
              ) : (
                <>
                  <Button onClick={() => checkout(p.id)} disabled={busy === p.id || current} className="mt-4 bg-[var(--primary)] hover:opacity-90" data-testid={`pricing-checkout-${p.id}`}>
                    {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : current ? "Current plan" : `Get ${p.name}`}
                  </Button>
                  {p.id === "architect" && !current && (
                    <Button variant="outline" onClick={() => checkout("architect_yearly")} disabled={busy === "architect_yearly"} className="mt-2 glass gold-edge gold-text" data-testid="pricing-checkout-architect-yearly">
                      {busy === "architect_yearly" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pay yearly · $299"}
                    </Button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <div>
        <h2 className="text-lg font-display font-semibold mb-3">One-time purchases</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {oneTime.map((c) => (
            <div key={c.id} className="litra-card p-5 flex flex-col" data-testid={`pricing-onetime-${c.id}`}>
              <div className="flex items-center justify-between"><span className="font-display font-semibold">{c.name}</span><span className="green-text font-display">${c.amount}</span></div>
              <p className="mt-1 text-sm text-[var(--bone-muted)] flex-1">{c.description}</p>
              <Button variant="outline" onClick={() => checkout(c.id)} disabled={busy === c.id} className="mt-3 glass border-[rgba(185,192,204,0.25)]" data-testid={`pricing-buy-${c.id}`}>
                {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy · $${c.amount}`}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
